import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Table, message } from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import DataKitApi from "../../../../api/DataKitApi";
import { DataKitDto, DataKitGroupDto, GroupMenuUsageDto } from "../../../../domain/DataKitDto";
import { DataKitGroupDrawer } from "./DataKitGroupDrawer";
import "./DataKitGroupsTab.scss";

const SEP = "\u001f";

const pathOf = (group: DataKitGroupDto) =>
  group.path && group.path.length > 0 ? group.path : [group.name];

const keyOf = (group: DataKitGroupDto) => pathOf(group).join(SEP);

interface Props {
  kit: DataKitDto;
  groups: DataKitGroupDto[];
  canWrite: boolean;
  onChanged: () => void;
}

/**
 * The Groups tab: a tree, because groups nest to any depth.
 *
 * The design was drawn one level deep, with a group's children shown as chips in a "Subgroups"
 * column. Nesting has no depth limit, so chips cannot represent it — this renders the real tree,
 * indented, with each row expandable. The Subgroups column keeps its place and reports how many
 * sit directly underneath.
 */
export function DataKitGroupsTab({ kit, groups, canWrite, onChanged }: Props) {
  const [usage, setUsage] = useState<GroupMenuUsageDto[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<DataKitGroupDto | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  const loadUsage = useCallback(() => {
    if (!kit.id) return;
    DataKitApi.listGroupMenuUsage(kit.id)
      .then((res) => setUsage(res.data || []))
      .catch(() => undefined);
  }, [kit.id]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  const menusByGroup = useMemo(() => {
    const map = new Map<string, string[]>();
    usage.forEach((u) => {
      const labels = (u.menuLabels || []).filter((l): l is string => !!l && l.trim() !== "");
      map.set(u.groupName, labels);
    });
    return map;
  }, [usage]);

  const childrenByParent = useMemo(() => {
    const map = new Map<string, DataKitGroupDto[]>();
    groups.forEach((g) => {
      const p = pathOf(g);
      const parentKey = p.slice(0, -1).join(SEP);
      const list = map.get(parentKey) || [];
      list.push(g);
      map.set(parentKey, list);
    });
    map.forEach((list) => list.sort((a, b) => a.name.localeCompare(b.name)));
    return map;
  }, [groups]);

  /** Roots first, then each expanded branch's children inline — a flattened visible tree. */
  const visible = useMemo(() => {
    const rows: { group: DataKitGroupDto; depth: number }[] = [];
    const walk = (parentKey: string, depth: number) => {
      (childrenByParent.get(parentKey) || []).forEach((group) => {
        rows.push({ group, depth });
        if (expanded.has(keyOf(group))) walk(keyOf(group), depth + 1);
      });
    };
    walk("", 0);
    return rows;
  }, [childrenByParent, expanded]);

  const toggle = (group: DataKitGroupDto) => {
    const key = keyOf(group);
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const createRoot = () => {
    const value = newName.trim();
    if (!value || !kit.id) return;
    setSaving(true);
    DataKitApi.createGroup(kit.id, { groupName: value, name: value, path: [value] })
      .then(() => {
        setCreateOpen(false);
        setNewName("");
        onChanged();
      })
      .catch(() => message.error("Could not create that group"))
      .finally(() => setSaving(false));
  };

  const columns = [
    {
      title: "Group",
      key: "name",
      width: 340,
      render: (_: unknown, row: { group: DataKitGroupDto; depth: number }) => {
        const childCount = (childrenByParent.get(keyOf(row.group)) || []).length;
        const isExpanded = expanded.has(keyOf(row.group));
        return (
          <div
            className="data-kit-group-name-cell"
            style={{ paddingLeft: row.depth * 20 }}
          >
            <button
              type="button"
              className={`data-kit-group-twisty${childCount === 0 ? " empty" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                if (childCount > 0) toggle(row.group);
              }}
              aria-label={isExpanded ? "Collapse" : "Expand"}
            >
              {childCount > 0 && (isExpanded ? <DownOutlined /> : <RightOutlined />)}
            </button>
            <div>
              <div className="data-kit-group-name">{row.group.name}</div>
              {row.group.note && <div className="data-kit-group-note">{row.group.note}</div>}
            </div>
          </div>
        );
      },
    },
    {
      title: "Subgroups",
      key: "subgroups",
      render: (_: unknown, row: { group: DataKitGroupDto }) => {
        const own = childrenByParent.get(keyOf(row.group)) || [];
        if (own.length === 0) return <span className="data-kit-group-empty">--</span>;
        return (
          <div className="data-kit-subgroup-chips">
            {own.map((g) => (
              <span key={keyOf(g)} className="data-kit-subgroup-chip">
                {g.name}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      title: "Used on",
      key: "usedOn",
      width: 320,
      render: (_: unknown, row: { group: DataKitGroupDto }) => {
        const labels = menusByGroup.get(row.group.name) || [];
        if (labels.length === 0) return <span className="data-kit-group-empty">--</span>;
        return labels.join(" • ");
      },
    },
    {
      title: "",
      key: "action",
      width: 90,
      align: "right" as const,
      render: (_: unknown, row: { group: DataKitGroupDto }) =>
        canWrite ? (
          <Button
            className="data-kit-group-edit-btn"
            size="small"
            onClick={() => setEditing(row.group)}
          >
            Edit
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="data-kit-groups-tab">
      <div className="data-kit-groups-header">
        <p className="data-kit-groups-hint">
          Name + note live on the group &mdash; edits reach every design using it
        </p>
        {canWrite && (
          <Button
            className="data-kit-group-edit-btn"
            size="small"
            onClick={() => {
              setNewName("");
              setCreateOpen(true);
            }}
          >
            + New Group
          </Button>
        )}
      </div>

      <Table
        className="data-kit-groups-table"
        dataSource={visible}
        columns={columns}
        rowKey={(row) => keyOf(row.group)}
        pagination={false}
        locale={{ emptyText: "No groups yet" }}
      />

      <Modal
        className={"data-kit-group-modal clean one"}
        open={createOpen}
        width={520}
        onCancel={() => setCreateOpen(false)}
      >
        <header>
          <h2>New group</h2>
        </header>
        <label className="data-kit-field">
          <span>Name</span>
          <Input
            size="large"
            placeholder="e.g. Prime Seafood"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onPressEnter={createRoot}
            maxLength={255}
            autoFocus
          />
          <span className="data-kit-field-hint">
            Nest groups inside it afterwards, from its own editor.
          </span>
        </label>

        <footer>
          <Button size="large" onClick={() => setCreateOpen(false)}>
            Cancel
          </Button>
          <Button
            size="large"
            className="primary"
            loading={saving}
            disabled={!newName.trim()}
            onClick={createRoot}
          >
            Create
          </Button>
        </footer>
      </Modal>

      {editing && (
        <DataKitGroupDrawer
          kit={kit}
          group={editing}
          groups={groups}
          canWrite={canWrite}
          onClose={() => setEditing(null)}
          onChanged={() => {
            onChanged();
            loadUsage();
          }}
          onNavigate={(group) => setEditing(group)}
        />
      )}
    </div>
  );
}
